import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Products from '../../components/cards/productsCard';
import Hero from '../../components/hero/';
import Title from '../../components/title/';
import Paragraph from '../../components/paragraph/';
import { getObjectsByType, getAttachments } from '../../utils/data';
import FilterProductsArea from '../../components/filter';
import { categoriesTypeOptions } from '../../components/filter';

const PER_PAGE = 50;
const CHUNK_SIZE = 200;

const Main = styled.main`
    margin-top:55px;
    background-color:${props => props.theme.colors[props.bgColor]};
`;

const LoadMoreArea = styled.div`
    display: flex;
    justify-content: center;
    padding: ${props => props.theme.spacing.doubleInset};
`;

const LoadMoreButton = styled.button`
    color: ${props => props.theme.colors.defaultPrimary};
    background-color: ${props => props.theme.colors.tertiary};
    border: none;
    border-radius: 3px;
    padding: ${props => props.theme.spacing.inset};
    font-size: 12px;
    font-weight: ${props => props.theme.fontWeight.mediumBold};
    cursor: pointer;
    text-transform: capitalize;
    transition: all 0.2s linear;

    &:hover:not(:disabled) {
        background-color: ${props => props.theme.colors.defaultPrimary};
        color: ${props => props.theme.colors.tertiary};
    }
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

async function fetchProductsWithImages(options) {
    const res = await getObjectsByType('product', options);
    if (!res || res.status === 404) return [];

    const productIDs = res.map(({ imageProduct }) => imageProduct).filter(img => typeof img === 'number');
    for (let i = 0; i < Math.ceil(productIDs.length / CHUNK_SIZE); i++) {
        const chunk = productIDs.splice(0, CHUNK_SIZE);
        const images = await getAttachments(chunk, 'medium');
        if (images?.length > 0) {
            images.forEach(img => {
                const post = res.find(p => p.imageProduct === parseInt(img.ID));
                if (post) post.imageProduct = img;
            });
        }
    }

    const ambientIDs = res.map(({ imageAmbient }) => imageAmbient).filter(img => typeof img === 'number');
    for (let i = 0; i < Math.ceil(ambientIDs.length / CHUNK_SIZE); i++) {
        const chunk = ambientIDs.splice(0, CHUNK_SIZE);
        const images = await getAttachments(chunk, 'medium');
        if (images?.length > 0) {
            images.forEach(img => {
                const post = res.find(p => p.imageAmbient === parseInt(img.ID));
                if (post) post.imageAmbient = img;
            });
        }
    }

    return res;
}

const MainArea = ({ dynamic_content = [] }) => {
    const [allPosts, setAllPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [category, setCategory] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const path = window.location.pathname.split('/').filter(el => el && el !== 'products' ? el : null);
        const newCategory = path.length > 0 && categoriesTypeOptions.find(obj => obj.value === path[0]) !== undefined ? path[0] : 'all';
        if (newCategory !== category) {
            setCategory(newCategory);
            setPage(0);
            setAllPosts([]);
        }
    });

    useEffect(() => {
        if (category === null) return;

        async function fetchData() {
            if (page === 0) {
                setLoading(true);
                window.scrollTo(0, 0);
            } else {
                setLoadingMore(true);
            }

            const options = { perPage: PER_PAGE, page };
            if (category !== 'all') {
                options.filter = { by: 'type', value: category };
            }

            try {
                const batch = await fetchProductsWithImages(options);
                setAllPosts(prev => page === 0 ? batch : [...prev, ...batch]);
                setHasMore(batch.length === PER_PAGE);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        }
        fetchData();
    }, [category, page]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Main bgColor="defaultQuarterly">
            {dynamic_content.map(({ acf_fc_layout, ...contentBlock }, j) => {
                if (acf_fc_layout === "heroContent") return <Hero key={j} {...contentBlock} />
                if (acf_fc_layout === "titleContent") return <Title key={j} {...contentBlock} />
                if (acf_fc_layout === "paragraphContent") return <Paragraph key={j} {...contentBlock} />
                return null;
            })}
            <FilterProductsArea category={category || ''} />
            <Products posts={allPosts} loading={loading} />
            {!loading && hasMore && (
                <LoadMoreArea>
                    <LoadMoreButton onClick={() => setPage(p => p + 1)} disabled={loadingMore}>
                        {loadingMore ? 'Loading...' : 'Load More'}
                    </LoadMoreButton>
                </LoadMoreArea>
            )}
        </Main>
    );
}
export default MainArea;
